import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import DynamicForm from "components/Screens/Setting/DynamicForm/DynamicForm";
import PageLoader from "components/common/PageLoader";

import { getLeavePolicyDetailsAPI } from "services/useTimeoffService";

import { mapApiValueToFormValue } from "components/Screens/Setting/utils";
import { getCountryFlag } from "components/Screens/CommonUtils";

import { TIME_OFF_ROUTES } from "constants/routes";
import { POLICY_FORM_SECTIONS } from "components/Screens/Setting/PolicyDetails/constants";
import { COUNTRIES } from "components/Screens/Setting/constants";

import "./PolicyDetails.scss";

function PolicyDetails({ policyId }) {
  const { countryName, policyType } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [policyDetails, setPolicyDetails] = useState({});
  const [error, setError] = useState(null);
  const [formValues, setFormValues] = useState(null);

  const selectedCountry = COUNTRIES[countryName] || "";
  const { leaveType = "" } = policyDetails;

  useEffect(() => {
    const fetchPolicyDetails = async () => {
      try {
        setLoading(true);
        const activePolicyId = policyId || 1;
        const { data } = await getLeavePolicyDetailsAPI(activePolicyId);
        setPolicyDetails(data);

        const mappedValues = {};

        POLICY_FORM_SECTIONS.forEach((section) => {
          section.fields.forEach(({ apiKey, apiKeys, name, type, customComponent }) => {
            if (apiKeys && customComponent) {
              const complexValue = {};

              Object.entries(apiKeys).forEach(([fieldKey, apiKeyName]) => {
                if (data[apiKeyName] !== undefined) {
                  complexValue[fieldKey] = mapApiValueToFormValue(data[apiKeyName], type, apiKey);
                }
              });
              mappedValues[name] = complexValue;
            }
            // Handle single apiKey fields
            else if (apiKey && data[apiKey] !== undefined) {
              if (name === "paidStatus" && apiKey === "unpaidAllowed") {
                mappedValues[name] = data[apiKey] === true ? "unpaid" : "paid";
              } else {
                mappedValues[name] = mapApiValueToFormValue(data[apiKey], type, apiKey);
              }
            }
          });
        });

        setFormValues(mappedValues);
      } catch (err) {
        setError(err.message || "Failed to fetch policy details");
      } finally {
        setLoading(false);
      }
    };

    fetchPolicyDetails();
  }, [policyType, policyId]);

  const handleSubmit = () => {
    navigate(TIME_OFF_ROUTES.COUNTRY_DETAIL(countryName));
  };

  const handleCancel = () => {
    navigate(TIME_OFF_ROUTES.COUNTRY_DETAIL(countryName));
  };

  if (loading) {
    return (
      <div className="policy-details-container">
        <PageLoader />
      </div>
    );
  }

  if (error) {
    return <div className="policy-details-container">Error: {error}</div>;
  }

  return (
    <div className="policy-details-container">
      <div className="country-header">
        <div className="country-flag mr-025">{getCountryFlag(selectedCountry, 24, 24)}</div>
        <div className="country-name">
          <div className="para-body-s-regular color-text-body-primary">{selectedCountry}</div>
          <div className="heading-h4-semibold ">{leaveType}</div>
        </div>
      </div>
      <div className="policy-details-form">
        {formValues && (
          <DynamicForm
            showActionButtons={false}
            sections={POLICY_FORM_SECTIONS}
            initialValues={formValues}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        )}
      </div>
    </div>
  );
}

export default PolicyDetails;
